import { type Context, createContext, useContext } from 'react';

/**
 * Creates both a context and a hook via which the context can be accessed.
 *
 * The context is nullable, so that it does not require default values.
 * However if the hook is used in a location, where the context is null, an error is thrown.
 *
 * @param contextName will be used in the error, when the context is not available
 */
export default function createContextHardened<T>(contextName: string): [Context<T | null>, () => T] {
  const NullableContext = createContext<T | null>(null);

  const useFn = (): T => {
    const contextObj = useContext(NullableContext);

    if (contextObj === null) {
      throw new Error('Called useContext outside of Context scope: ' + contextName);
    }

    return contextObj;
  };

  return [NullableContext, useFn];
}
